
import React from "react"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import { useTheme } from "~/ui/styled"
import { Pressable, ScrollView } from "~/ui/primitives"
import { dismissKeyboardOnPress } from "~utils/keyboard"
import { SafeAreaView } from "~/ui/safe-area"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { ColumnFlex, Text, Button } from "~components/El"
import { MultiLineTextField } from "~components/InputField"
import { Header } from "~components/header"
import useNavigation from "~hooks/useNavigation"
import { useStores } from "~models"
import { useModal } from "~hooks/useModal"
import { Event, EventNames, UserUpdateData, FLAG_ACK } from "~proto"
import { globalToast } from "~utils"
import { UpdateUserDisplayDescSchema } from "~schema"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

export interface UpdateCurrentUserDisplayDescFormProps {
  onDismiss: () => void
}

const UpdateCurrentUserDisplayDesc: React.FC<UpdateCurrentUserDisplayDescFormProps> = ({
  onDismiss,
}) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { eventStore, userStore } = useStores()
  const currentDesc = userStore.currentUser.desc
  const { Dialog } = useModal()
  const dismiss = () => {
    if (onDismiss) {
      onDismiss()
    } else {
      navigation.goBack()
    }
  }

  const publishUpdate = (desc: string) => {
    const evt = new Event<Partial<UserUpdateData>>(EventNames.USER_UPDATE, {
        desc,
    })
    eventStore.publish(evt, { flags: FLAG_ACK })
    globalToast.show("个性签名修改成功")
    dismiss()
  }

  const formik = useFormik({
    initialValues: { desc: currentDesc },
    validationSchema: UpdateUserDisplayDescSchema,
    onSubmit: (values) => {
        Dialog.show({
          title: "提示",
          content: "保存本次编辑?",
          onConfirm: () => publishUpdate(values.desc.trim()),
          onCancel: dismiss,
        })
    },
  })

  const { values, errors, touched, setFieldValue, setFieldTouched, handleSubmit, dirty, isValid } =
    formik

  const submitDisabled = !dirty || !isValid

  return (
    <SafeAreaView edges={["top", "bottom"]} style={{ flex: 1, minHeight: 0, overflow: "hidden", width: "100%", paddingTop: insets.top }}>
      <Pressable onClick={dismissKeyboardOnPress}>
        <Header
          headerText="个性签名"
          onLeftPress={dismiss}
          headerRight={
            <Button
              width="auto"
              height="30px"
              padding="0 12px"
              borderRadius="4px"
              bg={theme.colors.primary}
              onClick={() => handleSubmit()}
              disabled={submitDisabled}
            >
              <Text color={theme.colors.textLight} fontSize="14px">
                完成
              </Text>
            </Button>
          }
        />
      </Pressable>
      <ScrollView
        style={scrollViewFillStyle}
        keyboardShouldPersistTaps="never"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: insets.bottom + 16,
        }}
      >
        <ColumnFlex width="100%" padding="12px" bg={theme.colors.blockBackground}>
            <MultiLineTextField
                label="个性签名"
                value={values.desc}
                placeholder="请输入个性签名..."
                maxLength={200}
                style={{ height: 80 }}
                onChangeText={(v) => setFieldValue("desc", v)}
                onBlur={() => setFieldTouched("desc", true)}
                error={touched.desc && typeof errors.desc === "string" ? errors.desc : false}
            />
        </ColumnFlex>
      </ScrollView>
    </SafeAreaView>
  )
}

export default observer(UpdateCurrentUserDisplayDesc)
